import { Role, isAdmin, isSuperAdmin } from "@linkfluence/models/role";
import { User } from "@radarly/models/user";
import { isLinkfluenceManager, isRoot } from "../level";
import { isDraftInsightPage, isGlobalInsightPage } from "./is";
import { InsightPage, InsightPageAccess } from "./model";

export function isPublicPage(page: InsightPage) {
  return isPublicAccess(page.access);
}

export function isPrivatePage(page: InsightPage) {
  return isPrivateAccess(page.access);
}

export function isPrivateAccess(access: InsightPageAccess) {
  return access === InsightPageAccess.Private;
}

export function isAccessLocked(
  access: InsightPageAccess,
  sharedWith: string[] = []
): boolean {
  return isPrivateAccess(access) && sharedWith.length === 0;
}

export function isPublicAccess(access: InsightPageAccess) {
  return access === InsightPageAccess.Public;
}

export function isReadOnlyAccess(access: InsightPageAccess) {
  return access === InsightPageAccess.ReadOnly;
}

export function getAccessDefault(): InsightPageAccess {
  return InsightPageAccess.ReadOnly;
}

export function getAllAccess(): InsightPageAccess[] {
  return [
    InsightPageAccess.Private,
    InsightPageAccess.ReadOnly,
    InsightPageAccess.Public,
  ];
}

export function canEditInsightPage(page: InsightPage, user: User, role: Role) {
  return (
    isSupervisorOfAProjectOrLkfAdministrators(user, role) ||
    isDraftInsightPage(page) ||
    (isPublicPage(page) && !isGlobalInsightPage(page)) ||
    (isGlobalInsightPage(page) && isAdmin(role)) ||
    userIsAuthorOfInsightPage(user, page)
  );
}

export function canReadInsightPage(page: InsightPage, user: User, role: Role) {
  return (
    isSupervisorOfAProjectOrLkfAdministrators(user, role) ||
    userIsAuthorOfInsightPage(user, page) ||
    !isPrivatePage(page)
  );
}

// TODO remove check isDraftInsightPage() once share access feature is available
export function canEditInsightPageAccess(
  page: InsightPage,
  user: User,
  role: Role
) {
  return (
    isSupervisorOfAProjectOrLkfAdministrators(user, role) ||
    isDraftInsightPage(page) ||
    userIsAuthorOfInsightPage(user, page) ||
    (isGlobalInsightPage(page) && isPublicAccess(page.access))
  );
}

export function canDeleteInsightPage(
  page: InsightPage,
  user: User,
  role: Role
) {
  return (
    isSupervisorOfAProjectOrLkfAdministrators(user, role) ||
    userIsAuthorOfInsightPage(user, page) ||
    (isGlobalInsightPage(page) && isAdmin(role))
  );
}

export function canCloneInsightPage(page: InsightPage, user: User, role: Role) {
  if (isGlobalInsightPage(page)) {
    return canEditInsightPage(page, user, role);
  }
  return true;
}

export function canCreateProjectLevelInsightPage(user: User, role: Role) {
  return isSupervisorOfAProjectOrLkfAdministrators(user, role) || isAdmin(role);
}

function isSupervisorOfAProjectOrLkfAdministrators(user: User, role: Role) {
  return (
    isSuperAdmin(role) || isLinkfluenceManager(user.level) || isRoot(user.level)
  );
}

function userIsAuthorOfInsightPage(user: User, page: InsightPage) {
  return page.author === user.id;
}
